/**
 * Methodology Translations Module (Portuguese)
 * Research Approach & Data Sources for JCI Development Councils Youth Futures Observatory
 * @module methodology
 * @language pt
 */

export const methodology = {
  methodology: {
    // Page Meta
    pageTitle: "Abordagem de Pesquisa e Fontes de Dados",
    heroSubtitle: "Inteligência baseada em evidências capacitando Organizações Nacionais e Locais da JCI",
    heroDescription: "O Observatório de Futuros da Juventude dos Conselhos de Desenvolvimento da JCI é construído sobre uma metodologia rigorosa e auditável que combina dados verificados da organização JCI, iniciativas globais de pesquisa juvenil e índices econômicos estabelecidos.",
    
    // Hero CTAs
    ctaExplore: "Explorar a Metodologia",
    ctaContact: "Contatar a Equipe",
    
    // Executive Overview
    executiveOverviewTitle: "Resumo Executivo",
    executiveOverviewText: "Este documento descreve a abordagem de pesquisa, as fontes de dados e os métodos analíticos utilizados para criar os insights do observatório. Cada estatística exibida é rastreada diretamente a fontes verificadas e auditáveis.",
    coreCommitment: "Cada estatística exibida é rastreada diretamente a fontes verificadas e auditáveis.",
    
    // Overview Section
    overviewTitle: "O Framework DASHCity",
    overviewDescription: "Uma abordagem abrangente de 5 fases projetada para transformar organizações em potências de alto impacto através de avaliação sistemática, medição e melhoria contínua.",
    overviewIntro: "DASHCity fornece um caminho estruturado para as organizações identificarem oportunidades, medirem desempenho, comunicarem valor, estabelecerem governança e escalarem seu impacto globalmente.",
    frameworkFeatures: "Framework de 5 Fases DASHCity",
    inclusiveFeature: "Inclusivo - Projetado para organizações de todos os tamanhos e regiões",
    scalableFeature: "Escalável - Da implementação local à global",
    
    // Framework Characteristics
    characteristicsTitle: "Princípios do Framework",
    characteristics: {
      evidenceBased: "Baseado em Evidências",
      evidenceBasedDesc: "Fundamentado em dados verificados e pesquisa",
      iterative: "Iterativo",
      iterativeDesc: "Melhoria contínua através de ciclos de feedback",
      inclusive: "Inclusivo",
      inclusiveDesc: "Projetado para conselhos de todos os tamanhos e regiões",
      scalable: "Escalável",
      scalableDesc: "De organizações locais à implementação global",
      transparent: "Transparente",
      transparentDesc: "Metodologia aberta e padrões de dados",
      actionable: "Acionável",
      actionableDesc: "Ferramentas práticas e entregas claras"
    },
    
    // Phase Status Badges
    statusDone: "CONCLUÍDO",
    statusDoing: "EM ANDAMENTO",
    statusToDo: "PLANEJADO",
    
    // Phase Detail Labels
    activitiesLabel: "Atividades",
    deliverablesLabel: "Entregas",
    resultLabel: "Resultado",
    
    // Section Titles
    dataCollectionTitle: "Metodologia de Coleta de Dados",
    
    // Phase 1: IDENTIFY
    phase1Title: "IDENTIFICAR",
    phase1Period: "Setembro - Novembro 2025",
    phase1Objective: "Mapear o ecossistema completo de desenvolvimento de liderança juvenil",
    phase1Description: "A fase de fundação foca em entender o estado atual dos Conselhos de Desenvolvimento, mapear recursos existentes e identificar áreas-chave de oportunidade.",
    phase1Activities: "Mapeamento estratégico, diagnóstico institucional, análise de lacunas, definição do escopo de pesquisa",
    phase1Deliverables: "Documento de análise estratégica, Diagnóstico institucional, Inventário de fontes de dados, Documento de escopo de pesquisa",
    phase1KeyMetrics: "Taxa de cobertura de conselhos, nível de engajamento de stakeholders, áreas prioritárias identificadas",
    phase1Result: "Justificativa estratégica clara para a iniciativa de pesquisa",
    phase1Status: "done",
    
    // Phase 2: MEASURE
    phase2Title: "MEDIR",
    phase2Period: "Outubro - Dezembro 2025",
    phase2Objective: "Estabelecer padrões rigorosos de dados e processos de verificação",
    phase2Description: "A fase de medição implementa coleta rigorosa de dados em múltiplas fontes, estabelecendo métricas de base e indicadores de desempenho.",
    phase2Activities: "Padrões de qualidade de dados, verificação de fontes, estabelecimento de linha base, implementação do processo de validação",
    phase2Deliverables: "Padrões de qualidade de dados, Matriz de verificação de fontes, Documentação de metodologia YFI, 40+ pontos de dados auditáveis",
    phase2KeyMetrics: "Taxa de completude de dados, cobertura de indicadores, precisão de validação",
    phase2Result: "40+ pontos de dados auditáveis estabelecidos",
    phase2Status: "done",
    
    // Phase 3: PROMOTE
    phase3Title: "PROMOVER",
    phase3Period: "Dezembro 2025 - Janeiro 2026",
    phase3Objective: "Comunicar valor e engajar stakeholders",
    phase3Description: "A fase de promoção transforma insights de medição em narrativas convincentes que comunicam o valor e impacto do observatório.",
    phase3Activities: "Narrativa de valor estratégico, materiais de comunicação, canais de engajamento, alcance a stakeholders",
    phase3Deliverables: "Materiais multilíngues, Propostas de valor por stakeholder, Canais de engajamento, Guias de contribuição",
    phase3KeyMetrics: "Alcance de stakeholders, taxas de engajamento, pontuações de ressonância da mensagem",
    phase3Result: "Materiais de comunicação multilíngues operacionais",
    phase3Status: "doing",
    
    // Phase 4: DIRECT
    phase4Title: "DIRECIONAR",
    phase4Period: "Janeiro - Fevereiro 2026",
    phase4Objective: "Estabelecer governança e supervisão de qualidade",
    phase4Description: "A fase de direção estabelece frameworks robustos de governança, define papéis e responsabilidades, e cria estruturas operacionais sustentáveis.",
    phase4Activities: "Estrutura de governança, processo de validação, ciclos de atualização, definições de responsabilidade",
    phase4Deliverables: "Estrutura de governança, Definições de responsabilidade, Checklist de qualidade, Cronograma de ciclos de atualização",
    phase4KeyMetrics: "Taxa de adoção de governança, eficiência de decisão, níveis de conformidade",
    phase4Result: "Estrutura de governança com 4 Conselhos de Desenvolvimento",
    phase4Status: "doing",
    
    // Phase 5: SCALE
    phase5Title: "ESCALAR",
    phase5Period: "Janeiro 2026 - Dezembro 2027+",
    phase5Objective: "Expandir alcance, impacto e influência global",
    phase5Description: "A fase de escala foca em expandir o alcance da metodologia, amplificar impacto através de efeitos de rede e estabelecer o framework como referência global.",
    phase5Activities: "Expansão para 15+ ONs, APIs públicas, parcerias acadêmicas, influência em políticas",
    phase5Deliverables: "Plano de Rollout Global, APIs Públicas, Parcerias acadêmicas, Documentação de influência em políticas",
    phase5KeyMetrics: "Cobertura geográfica, usuários registrados, citações na mídia",
    phase5Result: "Plataforma escalando para status de autoridade global",
    phase5Status: "todo",
    
    // Data Sources Section
    dataSourcesTitle: "Fontes de Dados",
    dataSourcesDescription: "Dados abrangentes de fontes verificadas da JCI e pesquisa global",
    primaryDataSourcesTitle: "Fontes de Dados Primárias",
    jciInstitutionalData: "Dados Institucionais JCI",
    yfiGlobalData: "Índice de Futuros da Juventude e Dados Globais",
    secondarySourcesTitle: "Fontes de Dados Secundárias",
    
    // Table Column Headers
    sourceColumn: "Fonte",
    coverageColumn: "Cobertura",
    categoryColumn: "Categoria",
    dimensionColumn: "Dimensão",
    weightColumn: "Peso",
    sourcesColumn: "Fontes",
    criterionColumn: "Critério",
    exampleColumn: "Exemplo",
    gapColumn: "Lacuna",
    mitigationColumn: "Mitigação",
    
    // Primary Data Source Items
    sourceJCIImpactReport: "Relatório de Impacto JCI 2025",
    sourceJCIVision2040: "Plano Estratégico JCI Visão 2040",
    sourceJCIMembershipSurvey: "Pesquisa de Membros JCI 2025",
    coverageImpactReport: "148.122 membros, 4.641 OLs",
    coverageVision2040: "Metas estratégicas até 2040",
    coverageSurvey: "3.790 respondentes",
    categoryJciInstitutional: "Institucional JCI",

    // YFI Dimensions Table
    yfiDimensionsTitle: "Dimensões do Índice de Futuros da Juventude",
    dimensionMentalHealth: "Saúde Mental",
    dimensionWorkforce: "Força de Trabalho e Educação",
    dimensionEntrepreneurship: "Empreendedorismo",
    dimensionEducation: "Educação",
    coverageCountries: "114 países",
    sourceMentalHealth: "OMS, Sapien Labs MHQ",
    sourceWorkforce: "OIT, UNESCO, Banco Mundial",
    sourceEntrepreneurship: "Banco Mundial, GEM, ITU",
    
    // Formula Legend
    formulaLabel: "Fórmula do Índice de Futuros da Juventude",
    formulaLegendMH: "MH = Saúde Mental",
    formulaLegendWF: "WF = Força de Trabalho e Educação",
    formulaLegendENT: "ENT = Empreendedorismo",
    
    // DC Partnership
    dcPartnershipDescription: "Como os Conselhos de Desenvolvimento colaboram com o observatório",
    dcProvidedTitle: "O que os CDs Forneceram",
    dcNotProvidedTitle: "O que os CDs NÃO Forneceram",
    dcProvidedStrategic: "Colaboração estratégica, feedback regional",
    dcProvidedRegional: "Suporte de disseminação em todas as regiões",
    dcProvidedValidation: "Contexto regional sobre descobertas, confirmação de relevância",
    dcProvidedPlansOfAction: "Planos de Ação 2025, estratégias regionais",
    dcNotProvidedPrimaryData: "Dados de pesquisa primária",
    dcNotProvidedSurveyDesign: "Design ou execução de pesquisas",
    dcNotProvidedBaseline: "Coleta de dados de linha base",
    dcNotProvidedAnalysis: "Análise de dados",
    
    // Data Collection Section
    dataCollectionDescription: "Cinco áreas-chave de coleta e análise de dados",
    dataCollectionJCI: "Consolidação de Dados JCI",
    dataCollectionJCIDesc: "Reuniu Relatório de Impacto JCI 2025 oficial, extraiu dados de membros, organizou por região",
    dataCollectionSurvey: "Análise de Dados de Pesquisa",
    dataCollectionSurveyDesc: "Analisou 3.790 respondentes para demografia, motivações, lacunas de habilidades",
    dataCollectionVision: "Documentação Visão 2040",
    dataCollectionVisionDesc: "Extraiu metas de crescimento, pilares estratégicos, desenvolveu planejamento de cenários",
    dataCollectionYFI: "Integração YFI",
    dataCollectionYFIDesc: "Mapeou 114 países JCI para rankings YFI, agregou pontuações regionais",
    dataCollectionMentalHealth: "Análise Profunda de Saúde Mental",
    dataCollectionMentalHealthDesc: "Sintetizou dados da OMS, analisou pesquisa da Sapien Labs, conectou ao JCI RISE",
    
    // Verification Checklist Items
    verifySourceIdentifiable: "Fonte identificável",
    verifyDateConfirmed: "Data confirmada",
    verifyMethodologyDocumented: "Metodologia documentada",
    verifySampleSizeStated: "Tamanho da amostra indicado",
    verifyLimitationsAcknowledged: "Limitações reconhecidas",
    verifyCalculationsReproducible: "Cálculos reproduzíveis",
    
    // Zero Synthetic Data
    zeroSyntheticDescription: "O observatório não contém dados sintéticos",
    zeroSyntheticItem1: "Sem projeções apresentadas como fatos atuais",
    zeroSyntheticItem2: "Sem estatísticas geradas por modelos",
    zeroSyntheticItem3: "Sem valores interpolados",
    zeroSyntheticItem4: "Sem estimativas (a menos que explicitamente marcadas)",
    zeroSyntheticItem5: "Sem distribuições assumidas",
    
    // Acknowledged Limitations
    limitationSurveyBias: "Viés de resposta de pesquisa",
    mitigationSurveyBias: "Ponderação regional aplicada, viés documentado",
    limitationCoverage: "Lacunas de cobertura de países YFI",
    mitigationCoverage: "Médias regionais calculadas; lacunas anotadas",
    limitationLagging: "Indicadores defasados",
    mitigationLagging: "Dados mais recentes usados (2024-2025)",
    limitationLanguage: "Limitações de idioma",
    mitigationLanguage: "Tradução em 5 idiomas; viés em inglês anotado",
    
    // Vision 2040 Alignment
    vision2040AlignmentDescription: "Como o observatório suporta a visão estratégica da JCI",
    strategyWhereExpand: "Onde Expandir",
    strategyWhereExpandDesc: "Expansão prioritária em regiões da África e Oriente Médio com pontuações YFI abaixo de 55",
    strategyWhatPrioritize: "O que Priorizar",
    strategyWhatPrioritizeDesc: "Dimensão de Saúde Mental (peso 40%) como área de foco principal",
    strategyHowScale: "Como Escalar",
    strategyHowScaleDesc: "Meta de adoção tecnológica: 80%+ de engajamento digital",
    strategySuccessMeasures: "Medidas de Sucesso",
    strategySuccessMeasuresDesc: "Meta: Melhorar pontuações YFI regionais em 4-7 pontos até 2030",
    
    // Transparency Section
    transparencyDescription: "Transparência completa da metodologia e reprodutibilidade",
    reproducibilityTitle: "Reprodutibilidade",
    reproducibilityDescription: "Qualquer pesquisador pode reproduzir esta análise usando as fontes e metodologia publicadas",
    noProprietaryTitle: "Sem Métodos Proprietários",
    noProprietaryDescription: "Sem cálculos de caixa preta; todas as fórmulas e pesos estão documentados publicamente",
    
    // Summary Section
    summaryDescription: "Status atual da implementação da metodologia de pesquisa",
    summaryCompletedItem1: "Fase 1: Identificar",
    summaryCompletedItem2: "Fase 2: Medir",
    summaryCompletedItem3: "Arquitetura de dados",
    summaryCompletedItem4: "Cálculo YFI",
    summaryCompletedItem5: "Cobertura de 114 países",
    summaryCompletedItem6: "Plataforma multilíngue",
    summaryCompletedItem7: "Modelo de parceria DC",
    summaryInProgressItem1: "Fase 3: Promover",
    summaryInProgressItem2: "Fase 4: Direcionar",
    summaryInProgressItem3: "Integração de feedback de usuários",
    summaryInProgressItem4: "Análises regionais profundas",
    summaryPlannedItem1: "Fase 5: Escalar",
    summaryPlannedItem2: "Integração UN/WEF",
    summaryPlannedItem3: "Desenvolvimento de API",
    
    // Data Integrity Section
    dataIntegrityTitle: "Padrões de Integridade de Dados",
    verificationChecklistTitle: "Lista de Verificação",
    zeroSyntheticTitle: "Compromisso com Zero Dados Sintéticos",
    zeroSyntheticException: "Exceção: Projeções de cenários para a Visão 2040 são claramente rotuladas como 'cenários' e ilustrativas, não previsões.",
    acknowledgedLimitationsTitle: "Limitações Reconhecidas",
    
    // Verification Checklist Examples
    verifyExampleSource: "JCI Impact Report 2025, p.12",
    verifyExampleDate: "Dados de dezembro de 2024",
    verifyExampleMethodology: "Metodologia da pesquisa no apêndice",
    verifyExampleSampleSize: "N = 3.790 respondentes",
    verifyExampleLimitations: "Viés de idioma inglês observado",
    verifyExampleCalculations: "Fórmula YFI publicada",
    
    // DC Partnership Section
    dcPartnershipTitle: "Parceria com Conselhos de Desenvolvimento",
    partnershipModelTitle: "Modelo de Parceria",
    whatDCsProvided: "O que os CDs Forneceram",
    whatDCsDidNotProvide: "O que os CDs NÃO Forneceram",
    
    // Measurement Modules
    modulesTitle: "Módulos de Medição",
    modulesDescription: "Quatro áreas temáticas garantem avaliação abrangente do desempenho e impacto dos Conselhos de Desenvolvimento.",
    
    module1Title: "Alinhamento Estratégico",
    module1Description: "Mede alinhamento com missão, visão e prioridades estratégicas globais da JCI",
    module1Indicators: "Clareza da visão, adoção da missão, qualidade do plano estratégico, pontuação de alinhamento global",
    
    module2Title: "Excelência Operacional",
    module2Description: "Avalia eficiência operacional, gestão de recursos e saúde organizacional",
    module2Indicators: "Utilização de recursos, eficiência de processos, satisfação de membros, sustentabilidade financeira",
    
    module3Title: "Qualidade Educacional",
    module3Description: "Avalia programas de treinamento, desenvolvimento de habilidades e eficácia de transferência de conhecimento",
    module3Indicators: "Pontuações de qualidade de programas, resultados de aprendizagem, taxas de certificação, eficácia de instrutores",
    
    module4Title: "Impacto e Transformação",
    module4Description: "Mede resultados tangíveis, impacto na comunidade e mudança transformacional",
    module4Indicators: "Taxas de conclusão de projetos, alcance de beneficiários, alinhamento com ODS, métricas de sustentabilidade",
    
    // Areas of Opportunity
    areasTitle: "Áreas de Oportunidade",
    areasDescription: "A metodologia aborda quatro áreas interconectadas onde os Conselhos de Desenvolvimento podem maximizar seu impacto.",
    
    areaIndividual: "Desenvolvimento Individual",
    areaIndividualFocus: "Crescimento pessoal e desenvolvimento de liderança dos membros",
    areaIndividualMetrics: "Habilidades adquiridas, posições de liderança ocupadas, avanço de carreira",
    
    areaCommunity: "Impacto na Comunidade",
    areaCommunityFocus: "Mudança positiva em comunidades locais através de projetos e iniciativas",
    areaCommunityMetrics: "Projetos concluídos, beneficiários alcançados, resultados sustentáveis",
    
    areaInternational: "Conexão Internacional",
    areaInternationalFocus: "Engajamento global e colaboração intercultural",
    areaInternationalMetrics: "Eventos internacionais, parcerias formadas, participação em intercâmbios",
    
    areaEntrepreneurial: "Espírito Empreendedor",
    areaEntrepreneurialFocus: "Inovação, desenvolvimento de negócios e criação de oportunidades econômicas",
    areaEntrepreneurialMetrics: "Negócios apoiados, programas de inovação, impacto econômico",
    
    // Data Standards
    dataStandardsTitle: "Padrões de Dados",
    dataStandardsDescription: "Padrões rigorosos garantem qualidade, confiabilidade e comparabilidade de dados em todas as fontes.",
    
    acceptedTitle: "Critérios de Dados Aceitos",
    acceptedVerifiable: "Verificável através de fontes oficiais",
    acceptedAuditable: "Auditável com trilha de documentação clara",
    acceptedTraceable: "Rastreável ao ponto de coleta original",
    acceptedAccurate: "Preciso dentro dos níveis de tolerância definidos",
    acceptedCurrent: "Atual (coletado dentro do prazo especificado)",
    acceptedContextualized: "Contextualizado com metadados relevantes",
    acceptedCited: "Devidamente citado e atribuído",
    
    notAcceptedTitle: "Não Aceito",
    notAcceptedSynthetic: "Dados sintéticos ou gerados por IA sem validação",
    notAcceptedUnverifiable: "Alegações ou estimativas não verificáveis",
    notAcceptedProprietary: "Dados proprietários sem permissão",
    notAcceptedIncomplete: "Conjuntos de dados incompletos faltando variáveis-chave",
    notAcceptedBiased: "Amostras enviesadas não representativas da população",
    
    // Validation Process
    validationTitle: "Processo de Validação",
    validationDescription: "Uma validação rigorosa de 5 etapas garante integridade e confiabilidade dos dados.",
    
    validationStep1: "Coleta Inicial",
    validationStep1Desc: "Dados coletados usando instrumentos e protocolos padronizados",
    validationStep2: "Verificação de Qualidade",
    validationStep2Desc: "Procedimentos automatizados e manuais de garantia de qualidade",
    validationStep3: "Validação Cruzada",
    validationStep3Desc: "Verificação contra múltiplas fontes de dados",
    validationStep4: "Revisão de Especialistas",
    validationStep4Desc: "Avaliação e aprovação de especialistas no assunto",
    validationStep5: "Certificação Final",
    validationStep5Desc: "Certificação oficial e aprovação para publicação",
    
    // Governance Structure
    governanceTitle: "Proposta de Estrutura de Governança",
    governanceDescription: "Governança clara garante responsabilização, transparência e tomada de decisão eficaz.",
    
    governanceLevel1: "Conselho Internacional JCI",
    governanceLevel1Desc: "Supervisão estratégica e direção de políticas",
    governanceLevel2: "Conselhos de Desenvolvimento",
    governanceLevel2Desc: "Validação de dados e coordenação regional",
    governanceLevel3: "Equipe Executiva",
    governanceLevel3Desc: "Operações e implementação",
    governanceLevel4: "Contribuidores e Usuários",
    governanceLevel4Desc: "Feedback da comunidade e engajamento",
    
    // Update Cycles
    updateCyclesTitle: "Ciclos de Atualização",
    updateCycleAnnual: "Anual",
    updateCycleAnnualDesc: "Atualização completa de dados (coleta Nov, validação Dez, publicação Jan)",
    updateCycleIntermediate: "Intermediário",
    updateCycleIntermediateDesc: "Nova pesquisa significativa ou inovações de programas",
    updateCycleContinuous: "Contínuo",
    updateCycleContinuousDesc: "Correções de erros, atualizações de links, melhorias de plataforma",
    
    // Vision 2040 Alignment
    vision2040AlignmentTitle: "Alinhamento com JCI Visão 2040",
    strategicIntelligenceTitle: "Função de Inteligência Estratégica",
    strategicRecommendationsTitle: "Recomendações Estratégicas",
    
    // Transparency Section
    transparencyTitle: "Transparência e Reprodutibilidade",
    completeSourceInventoryTitle: "Inventário Completo de Fontes",
    jciDocuments: "Documentos Oficiais JCI",
    globalResearchSources: "Fontes de Pesquisa Global",
    formulaReproducibility: "Qualquer pesquisador pode reproduzir esta análise",
    formulaExplanation: "FII = (0.40 × MH) + (0.30 × WF) + (0.30 × ENT)",
    fullTransparency: "Sem metodologias proprietárias, sem cálculos de caixa preta, sem suposições ocultas.",
    
    // Summary Section
    summaryTitle: "Status da Metodologia de Pesquisa",
    summaryCompleted: "CONCLUÍDO",
    summaryInProgress: "EM ANDAMENTO",
    summaryPlanned: "PLANEJADO",
    
    // Expansion Roadmap
    roadmapTitle: "Roadmap de Expansão",
    roadmapDescription: "Uma abordagem faseada para adoção global e melhoria contínua.",
    
    roadmap2025: "2025: Fundação",
    roadmap2025Desc: "Implementação piloto com 4 Conselhos de Desenvolvimento, padrões de dados estabelecidos",
    roadmap2026: "2026: Expansão (BETA)",
    roadmap2026Desc: "15+ ONs contribuindo, 200+ usuários, APIs públicas para pesquisadores",
    roadmap2027: "2027+: Autoridade Global",
    roadmap2027Desc: "Integração UN/WEF/UNESCO, 1,000+ usuários, influência em políticas de 10+ países",
    
    // Success Factors
    successTitle: "Fatores de Sucesso",
    successDescription: "Elementos-chave que contribuem para implementação bem-sucedida da metodologia.",
    
    successFactor1: "Patrocínio e compromisso executivo forte",
    successFactor2: "Comunicação clara e gestão de mudanças",
    successFactor3: "Recursos adequados e equipes dedicadas",
    successFactor4: "Abordagem iterativa com feedback contínuo",
    successFactor5: "Habilitação tecnológica e automação",
    successFactor6: "Sensibilidade cultural e adaptação regional",
    
    // Lessons Learned
    lessonsTitle: "Lições Aprendidas",
    lessonsDescription: "Insights de implementações piloto e feedback de stakeholders.",
    
    lesson1: "Começar com adotantes iniciais dispostos para construir momentum",
    lesson2: "Investir fortemente em treinamento e capacitação",
    lesson3: "Equilibrar padronização com flexibilidade local",
    lesson4: "Celebrar vitórias rápidas para manter engajamento",
    lesson5: "Incorporar mecanismos de feedback desde o início",
    
    // FAQ Section
    faqTitle: "FAQ da Metodologia",
    faqDescription: "Perguntas comuns sobre a metodologia de pesquisa.",
    
    faqQ1: "Quais fontes de dados o observatório utiliza?",
    faqA1: "O observatório combina três fontes primárias: dados institucionais JCI (Relatório de Impacto, Pesquisa de Membros, Visão 2040), o Índice de Futuros da Juventude cobrindo 114 países, e pesquisa global da OMS, OIT, UNESCO, Banco Mundial e outras fontes estabelecidas.",
    
    faqQ2: "Como a qualidade dos dados é garantida?",
    faqA2: "Todos os dados devem passar por uma lista de verificação de 7 critérios: verificável, auditável, rastreável, preciso, atual, contextualizado e devidamente citado. Um processo de validação de 5 etapas garante a integridade antes da publicação.",
    
    faqQ3: "O que significa 'zero dados sintéticos'?",
    faqA3: "Não são apresentadas projeções, estatísticas geradas por modelos, valores interpolados ou estimativas como fatos. Todas as métricas exibidas são rastreadas diretamente a fontes verificadas. A única exceção são os cenários de Visão 2040, claramente rotulados como ilustrativos.",
    
    faqQ4: "Com que frequência os dados são atualizados?",
    faqA4: "O observatório segue três ciclos de atualização: atualização completa anual (Nov-Jan), atualizações intermediárias para nova pesquisa significativa, e melhorias contínuas para correções e atualizações de plataforma.",
    
    faqQ5: "Os pesquisadores podem reproduzir a análise?",
    faqA5: "Sim. Todos os documentos fonte estão publicamente disponíveis, a lógica de agregação está documentada e a fórmula FII (0.40×MH + 0.30×WF + 0.30×ENT) é transparente. Qualquer pesquisador pode reproduzir e verificar os resultados.",
    
    // CTA Section
    ctaTitle: "Pronto para Explorar o Observatório?",
    ctaDescription: "Acesse insights baseados em evidências para apoiar as decisões estratégicas da sua organização.",
    ctaButton1: "Explorar o Dashboard",
    ctaButton2: "Como Contribuir",
    ctaButton3: "Ler o FAQ",
    
    // Source Attribution
    sourceTitle: "Fonte",
    sourceDescription: "Metodologia Versão 3.1, Janeiro 2026",
    sourceNote: "Desenvolvido pelos Conselhos de Desenvolvimento JCI em colaboração com stakeholders globais"
  }
} as const;
